import { Button, Link, twMerge } from '@sortlist-frontend/design-system';
import { ArrowRight } from '@sortlist-frontend/design-system/icons';
import { useEffect, useState } from 'react';

import type { GuideCta, GuidePageContent } from '../types';

interface GuideTocProps {
  toc: NonNullable<GuidePageContent['toc']>;
  cta: GuideCta;
}

export const GuideToc = (props: GuideTocProps) => {
  const { toc, cta } = props;
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: '-25% 0px -65% 0px' },
    );
    toc.entries.forEach((entry) => {
      const element = document.getElementById(entry.id);
      if (element != null) observer.observe(element);
    });
    return () => observer.disconnect();
  }, [toc.entries]);

  return (
    <nav aria-label={toc.title} className="rounded-2xl border border-secondary-200 bg-white p-5 lg:sticky lg:top-24">
      <p className="text-sm font-semibold uppercase tracking-wide text-secondary-500">{toc.title}</p>
      <ol className="mt-3 space-y-1 text-sm">
        {toc.entries.map((entry) => (
          <li key={entry.id}>
            <Link
              href={`#${entry.id}`}
              color="foreground"
              underline="hover"
              className={twMerge(
                'block rounded-md px-2 py-1 text-sm',
                active === entry.id ? 'bg-primary-50 font-semibold text-primary-600' : 'text-secondary-700',
              )}>
              {entry.label}
            </Link>
          </li>
        ))}
      </ol>
      <div className="mt-5">
        <Button as="a" href={cta.href} color="primary" variant="solid" size="md" endContent={<ArrowRight />}>
          {cta.label}
        </Button>
      </div>
    </nav>
  );
};
